import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as serviceActions from '@redux/modules/service'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/service'
import type { ServiceModel } from '@models/types'

const stringifyObjectKeys: (keyof ServiceModel)[] = ['options']

export const generateUrls = () => ({
  servicesIndexUrl: '#/systemManager/services',
})

type UpdateServiceParams = {
  dispatch: AppDispatch,
  service: ServiceModel,
  serviceParams: Partial<ServiceModel>,
  requestOptions?: EntityOptions,
}

const updateService = (params: UpdateServiceParams) => {
  const {
    service, serviceParams, dispatch, requestOptions,
  } = params

  const { updateService: updateFn } = serviceActions

  const updatedParams = mergeAndStringifyEntityObjects(service, serviceParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type EditServiceParams = {
  service: ServiceModel,
  showEditServiceModal: Function,
}

const editService = (params: EditServiceParams) => new Promise((resolve, reject) => {
  const { service, showEditServiceModal } = params

  if (showEditServiceModal){
    const payload = {
      service,
    }

    showEditServiceModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showEditServiceModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useServiceForm(
  service: Partial<ServiceModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options

  const serviceForm = useForm(
    defaultFormState,
    { entity: service, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [service.id, service.cache_key],
  )

  return serviceForm
}

function useService(initEntity: Partial<ServiceModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: service } = useLatestEntity(initEntity, 'services')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showEditServiceModal } = callbacks || {}

  const { loading, updating } = useSelector(reduxState => reduxState.services)

  const isAttachable: boolean = digObject(service, 'options.attachable')
  const isCategorizable: boolean = digObject(service, 'options.categorizable')
  const isForDigital: boolean = digObject(service, 'options.digital')
  const isForEditorial: boolean = digObject(service, 'options.editorial')
  const isForProposal: boolean = digObject(service, 'options.proposal')
  const isForPublication: boolean = digObject(service, 'options.publication')
  const isForSocialMedia: boolean = digObject(service, 'options.social_media')
  const isImageable: boolean = digObject(service, 'options.imageable')
  const isTaskable: boolean = digObject(service, 'options.taskable')
  const isTemplatable: boolean = digObject(service, 'options.templatable')

  return {
    callbacks: {
      // @ts-expect-error TS2322
      editService: () => editService({ service, showEditServiceModal }),
      updateService: (serviceParams: Partial<ServiceModel>, requestOptions?: EntityOptions) => updateService({
        // @ts-expect-error TS2322
        service, serviceParams, dispatch, requestOptions,
      }),
    },
    loading,
    isAttachable,
    isCategorizable,
    isForDigital,
    isForEditorial,
    isForProposal,
    isForPublication,
    isForSocialMedia,
    isImageable,
    isTaskable,
    isTemplatable,
    service,
    urls: generateUrls(),
    updating,
  }
}

export default useService
